import { Link } from "react-router-dom";
import { categories, credibilityMap, leads } from "@/mocks/leads";

type Lead = (typeof leads)[number];

interface LeadCardProps {
  lead: Lead;
  variant?: "grid" | "list";
}

export default function LeadCard({ lead, variant = "grid" }: LeadCardProps) {
  const cat = categories.find((c) => c.id === lead.category);
  const cred = credibilityMap[lead.credibility];

  if (variant === "list") {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row">
        <Link
          to={`/leads/${lead.id}`}
          className="relative h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-32 sm:w-52"
          aria-label={lead.title}
        >
          <img
            src={lead.cover}
            alt={`${lead.title} — 线索配图`}
            title={`${lead.title} 蝌蚪爆料`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${cred.className}`}>
              {cred.label}
            </span>
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-900">
              {cat?.name}
            </span>
          </div>
          <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950">
            <Link to={`/leads/${lead.id}`} className="transition-colors hover:text-primary-600">
              {lead.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {lead.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-voice-line" aria-hidden="true" />
              {lead.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true" />
              {lead.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-timer-line" aria-hidden="true" />
              {lead.readTime}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={`/leads/${lead.id}`}
        className="relative h-48 w-full overflow-hidden"
        aria-label={lead.title}
      >
        <img
          src={lead.cover}
          alt={`${lead.title} — 线索配图`}
          title={`${lead.title} 蝌蚪爆料`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-medium ${cred.className}`}>
          {cred.label}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <i className={`${cat?.icon} text-secondary-600`} aria-hidden="true" />
          <span className="font-medium text-secondary-700">{cat?.name}</span>
        </div>
        <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-foreground-950">
          <Link to={`/leads/${lead.id}`} className="transition-colors hover:text-primary-600">
            {lead.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
          {lead.summary}
        </p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-voice-line" aria-hidden="true" />
            {lead.source}
          </span>
          <Link
            to={`/leads/${lead.id}`}
            className="flex items-center gap-1 font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            阅读全文
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}